import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from '@rehooks/local-storage'
import { Analytics, BILLING_ROUTE, PRODUCTS_ROUTE } from '@ucc/unified-admin-common'

import { ConnectedUserContext, OrganizationContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import config from 'config'
import { mixpanelEventNames } from 'utils'
import { RESOLVE_TRIAL_ROUTE } from 'utils/constants'
import { isFreeOrTrialNonAddonResolveSubscription } from 'utils/subscriptionUtils'
import { useIsActiveUrl, usePartnerFeatures } from './index'

const IGNORED_PAGES = [`/${BILLING_ROUTE}`, `/${PRODUCTS_ROUTE}/${RESOLVE_TRIAL_ROUTE}`]

export interface ResolveUpgradeBannerResult {
  handleHideBanner: () => void
  resolveBannerVisible: boolean
}

const useResolveUpgradeBanner = (): ResolveUpgradeBannerResult => {
  const organizationContext = useContext(OrganizationContext)
  const connectedUserContext = useContext(ConnectedUserContext)
  const { accountKey: selectedAccountKey } = useAccountContext()
  const [resolveBannerVisible, setResolveBannerVisible] = useState<boolean>(false)
  const [dismissedBanners, setDismissedBanners] = useLocalStorage<string[]>(config.DISMISSED_BANNERS, [])
  const { isIngramAccount } = usePartnerFeatures()

  const isDismissedByUser = dismissedBanners.includes('resolve-upgrade-banner')
  const hasFreeOrTrialResolveSubscription = organizationContext?.subscriptions?.some(
    isFreeOrTrialNonAddonResolveSubscription,
  )

  const isIgnoredPageOpen = useIsActiveUrl(IGNORED_PAGES)

  const isAPartnerAccount = useMemo(() => {
    const connectedAccount = connectedUserContext?.accounts?.find((acc) => acc.key === selectedAccountKey)
    return connectedAccount?.parent || !!connectedAccount?.parentKey?.length
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedUserContext?.key, selectedAccountKey])

  useEffect(() => {
    const visible =
      !isDismissedByUser &&
      !isAPartnerAccount &&
      !!hasFreeOrTrialResolveSubscription &&
      !isIngramAccount &&
      !isIgnoredPageOpen
    setResolveBannerVisible(visible)

    if (visible) {
      Analytics.track(mixpanelEventNames.RESOLVE_CONVERSION, { Action: 'Banner is displayed', selectedAccountKey })
    }
  }, [
    isDismissedByUser,
    resolveBannerVisible,
    isAPartnerAccount,
    hasFreeOrTrialResolveSubscription,
    selectedAccountKey,
    isIngramAccount,
    isIgnoredPageOpen,
  ])

  const handleHideBanner = useCallback(() => {
    Analytics.track(mixpanelEventNames.RESOLVE_CONVERSION, { Action: 'Banner is closed', selectedAccountKey })
    setDismissedBanners([...dismissedBanners, 'resolve-upgrade-banner'])
    setResolveBannerVisible(false)
  }, [dismissedBanners, selectedAccountKey, setDismissedBanners, setResolveBannerVisible])

  return {
    resolveBannerVisible: !isDismissedByUser && resolveBannerVisible,
    handleHideBanner,
  } as ResolveUpgradeBannerResult
}

export default useResolveUpgradeBanner
